import React, { useEffect, useState } from "react";
import { X, Sparkles, ArrowRight, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";

const STORAGE_PREFIX = "cf_nudge_seen_";

/**
 * Auto-shows once at 7 days remaining and once at 3 days remaining.
 * After dismissal, that specific nudge won't re-show this trial cycle.
 */
const TrialNudgeModal = ({ trial }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    if (!trial || trial.paid || trial.expired || !trial.active) return;
    const days = trial.days_remaining ?? 0;
    let key = null;
    if (days <= 3) key = "3day";
    else if (days <= 7) key = "7day";
    if (!key) return;
    const seen = window.localStorage.getItem(STORAGE_PREFIX + key);
    if (seen) return;
    setVariant(key);
    setOpen(true);
  }, [trial]);

  const dismiss = () => {
    if (variant) {
      window.localStorage.setItem(STORAGE_PREFIX + variant, "1");
    }
    setOpen(false);
  };

  const goPricing = () => {
    dismiss();
    navigate("/#pricing");
    setTimeout(() => {
      const el = document.getElementById("pricing");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 250);
  };

  if (!open || !variant) return null;

  const config = {
    "7day": {
      kicker: "1 week left in your free trial",
      headline: "Lock in your empire before the trial ends.",
      sub:
        "You've still got room to play. Upgrade now and we'll carry your work, streak, and Tam tokens straight across — no interruption.",
      ctaLabel: "See plans",
      tone: "cyan",
    },
    "3day": {
      kicker: "3 days left — don't lose momentum",
      headline: "Your work is good. The trial isn't forever.",
      sub:
        "When the trial ends, content drafting and DMs pause. Upgrade today and lock Tam in for £9.99/mo.",
      ctaLabel: "Upgrade now",
      tone: "amber",
    },
  }[variant];

  const accent =
    config.tone === "amber"
      ? "from-amber-500/20 via-[#0A192F] to-[#0A192F] border-amber-400/40"
      : "from-cyan-500/15 via-[#0A192F] to-[#0A192F] border-cyan-400/40";

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center px-4"
      onClick={dismiss}
      data-testid="trial-nudge-overlay"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid={`trial-nudge-modal-${variant}`}
        className={`relative w-full max-w-md bg-gradient-to-br ${accent} border cf-glow rounded-sm p-7`}
      >
        <button
          onClick={dismiss}
          data-testid="trial-nudge-close"
          className="absolute top-3 right-3 text-slate-400 hover:text-white"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="flex items-center gap-2 text-cyan-300 font-mono text-[10px] uppercase tracking-[0.3em] mb-4">
          <Clock size={12} /> {config.kicker}
        </div>

        <h3
          className="font-heading text-3xl font-black text-white tracking-tighter leading-tight"
          data-testid="trial-nudge-headline"
        >
          {config.headline}
        </h3>

        <p className="mt-4 text-slate-300 text-sm leading-relaxed">{config.sub}</p>

        <div className="mt-6 flex flex-col sm:flex-row gap-2">
          <button
            onClick={goPricing}
            data-testid="trial-nudge-cta"
            className="flex-1 px-5 py-3 bg-[#00F0FF] text-[#050B14] font-bold text-xs uppercase tracking-widest rounded-sm hover:bg-[#33F3FF] flex items-center justify-center gap-2"
          >
            <Sparkles size={14} /> {config.ctaLabel}
            <ArrowRight size={14} />
          </button>
          <button
            onClick={dismiss}
            data-testid="trial-nudge-dismiss"
            className="px-5 py-3 text-xs uppercase tracking-widest text-slate-400 hover:text-white"
          >
            Not yet
          </button>
        </div>
      </div>
    </div>
  );
};

export default TrialNudgeModal;
